import { settingsActions } from '$lib/stores/settings';

export type ThemeMode = 'light' | 'dark' | 'auto';

/**
 * Service for managing theme toggling functionality
 */
export class ThemeToggleService {
  private static instance: ThemeToggleService;
  
  static getInstance(): ThemeToggleService {
    if (!this.instance) {
      this.instance = new ThemeToggleService();
    }
    return this.instance;
  }

  /**
   * Get the next theme in cycle: light -> dark -> auto
   */
  getNextTheme(currentTheme: ThemeMode): ThemeMode {
    const themeOrder: ThemeMode[] = ['light', 'dark', 'auto'];
    const currentIndex = themeOrder.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themeOrder.length;
    return themeOrder[nextIndex];
  }

  /**
   * Apply theme to document
   */
  applyTheme(theme: ThemeMode): void {
    const root = document.documentElement;
    
    if (theme === 'auto') {
      root.classList.remove('light-theme', 'dark-theme');
    } else if (theme === 'light') {
      root.classList.remove('dark-theme');
      root.classList.add('light-theme');
    } else {
      root.classList.remove('light-theme');
      root.classList.add('dark-theme');
    }
    
    // Update settings store
    settingsActions.updateEditor({ theme });
  }

  /**
   * Get theme status message
   */
  getThemeMessage(theme: ThemeMode): string {
    const themeMessages = {
      'light': '☀️ 라이트 모드로 전환되었습니다',
      'dark': '🌙 다크 모드로 전환되었습니다',
      'auto': '🔄 자동 모드로 전환되었습니다'
    };
    
    return themeMessages[theme];
  }

  /**
   * Get theme toggle tooltip
   */
  getThemeTooltip(currentTheme: ThemeMode): string {
    if (currentTheme === 'light') {
      return '다크 모드로 전환';
    } else if (currentTheme === 'dark') {
      return '자동 모드로 전환';
    } else {
      return '라이트 모드로 전환';
    }
  }

  /**
   * Toggle theme and return new theme
   */
  toggleTheme(currentTheme: ThemeMode): ThemeMode {
    const nextTheme = this.getNextTheme(currentTheme);
    this.applyTheme(nextTheme);
    return nextTheme;
  }
}

export const themeToggleService = ThemeToggleService.getInstance();